import {
  createFileRoute,
  Outlet,
  stripSearchParams,
} from "@tanstack/react-router";
import { listQuestionnaireSubmission } from "api-client";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import z from "zod";

import { createNavigationTabsContext } from "src/components/NavigationTabs";
import { NestedTabLink } from "src/layout/TabLink";
import { loaderHandler } from "src/lib/loaderHandler";

const RouteSearchSchema = z.object({
  tab: z.union([z.literal("list"), z.literal("response")]).default("list"),
});

export const Route = createFileRoute(
  "/_auth/_coach/questionnaires/$questionnaireId/submissions",
)({
  component: RouteComponent,
  ssr: true,
  validateSearch: RouteSearchSchema,
  search: {
    middlewares: [stripSearchParams({ tab: "list" })],
  },
  async loader({ params }) {
    return await loaderHandler(async () => {
      const questionnaireResponses = await listQuestionnaireSubmission({
        path: {
          questionnaireId: params.questionnaireId,
        },
      }).then((response) => {
        return response.data.data;
      });

      return {
        data: {
          questionnaireResponses: questionnaireResponses,
        },
      };
    });
  },
});

export const SubmissionsNavigationTabs = createNavigationTabsContext();

function RouteComponent() {
  const { t } = useTranslation();

  const loaderData = Route.useLoaderData();
  const params = Route.useParams();
  const search = Route.useSearch();

  const contextValue = useMemo(() => {
    return {
      tabs: [
        {
          tabId: "list",
          label: t("athleteSubmissions.tabs.summary"),
          href: `/questionnaires/${params.questionnaireId}/submissions?tab=${"list"}`,
        },
        {
          tabId: "response",
          label: t("athleteSubmissions.tabs.individual"),
          href: `/questionnaires/${params.questionnaireId}/submissions/${loaderData.data.questionnaireResponses.currentPage.at(0)?.id}?tab=${"response"}`,
        },
      ],
      components: { Link: NestedTabLink },
      getCurrentTabId: () => {
        return search.tab;
      },
    };
  }, [params, t, search.tab, loaderData.data.questionnaireResponses]);

  return (
    <SubmissionsNavigationTabs.Context.Provider value={contextValue}>
      <div className="w-full">
        <SubmissionsNavigationTabs.TabsRoot className="mx-auto max-w-[720px] w-full py-4">
          <SubmissionsNavigationTabs.TabsContent className="m-0 px-4">
            <div className="p-4 border-t border-x rounded-t-md flex flex-col gap-4">
              {t("athleteSubmissions.responsesCount", {
                count: loaderData.data.questionnaireResponses.pageInfo.count,
              })}
              {loaderData.data.questionnaireResponses.currentPage.length ===
              0 ? null : (
                <SubmissionsNavigationTabs.TabsList></SubmissionsNavigationTabs.TabsList>
              )}
            </div>
            <Outlet />
          </SubmissionsNavigationTabs.TabsContent>
        </SubmissionsNavigationTabs.TabsRoot>
      </div>
    </SubmissionsNavigationTabs.Context.Provider>
  );
}
